import axios from 'axios';
import { settings } from '@/plugins/settings';

export const IDENTITY_INTEGRATION_STATUS_ACTIVE = 'active';
export const IDENTITY_INTEGRATION_STATUS_INACTIVE = 'inactive';
export const IDENTITY_INTEGRATION_PROTOCOL_SAML = 'saml';

// The canonical Smart Point identity fields an Integration can map. Never a
// provider's own vocabulary: what the IdP calls each of these is configuration
// (source_name), which is the whole point of the mapping boundary.
export const CANONICAL_FIELD_EXTERNAL_SUBJECT = 'external_subject';
export const CANONICAL_FIELD_EMAIL = 'email';
export const CANONICAL_FIELD_FIRST_NAME = 'first_name';
export const CANONICAL_FIELD_LAST_NAME = 'last_name';
export const CANONICAL_FIELD_COMMUNITY_REFERENCE = 'community_reference';

export const CANONICAL_FIELDS = [
  CANONICAL_FIELD_EXTERNAL_SUBJECT,
  CANONICAL_FIELD_EMAIL,
  CANONICAL_FIELD_FIRST_NAME,
  CANONICAL_FIELD_LAST_NAME,
  CANONICAL_FIELD_COMMUNITY_REFERENCE,
];

export const ATTRIBUTE_SOURCE_NAME_ID = 'name_id';
export const ATTRIBUTE_SOURCE_ATTRIBUTE = 'attribute';

export const COMMUNITY_RESOLUTION_FIXED = 'fixed';
export const COMMUNITY_RESOLUTION_ATTRIBUTE_MAPPING = 'attribute_mapping';

export interface IIdentityIntegrationCertificateSummary {
  configured: boolean;
  usable: boolean;
  fingerprint_sha256: string | null;
  subject: string | null;
  issuer: string | null;
  valid_from: string | null;
  valid_to: string | null;
}

export interface IIdentityAttributeMapping {
  canonical_field: string;
  source_type: string;
  source_name: string | null;
  required: boolean;
}

export interface IIdentityIntegrationMapping {
  attributes: IIdentityAttributeMapping[];
  // Derived server-side from the email mapping; read-only here.
  email_attribute: string | null;
}

export interface IIdentityCommunityResolution {
  mode: string;
  mapping_count: number;
}

export interface IIdentityCommunityMapping {
  id: number;
  external_value: string;
  community_id: number;
}

export interface IIdentityIntegrationValidation {
  activatable: boolean;
  issues: string[];
}

/**
 * Whether the integration has a developer-portal PIN — never the PIN itself.
 * This is what the detail response carries; reading the PIN is a separate,
 * deliberate request. See sp-identity ADR-0015.
 */
export interface IIdentityIntegrationPortalPinSummary {
  configured: boolean;
  usable: boolean;
}

export interface IIdentityIntegrationPortalPin extends IIdentityIntegrationPortalPinSummary {
  pin: string | null;
}

export interface IIdentityIntegration {
  id: number;
  uuid: string;
  name: string;
  slug: string;
  status: string;
  protocol: string;
  issuer: string | null;
  idp_sso_url: string | null;
  sp_entity_id: string | null;
  jit_provisioning_enabled: boolean;
  identity_mapping: IIdentityIntegrationMapping;
  community_resolution: IIdentityCommunityResolution;
  certificate: IIdentityIntegrationCertificateSummary;
  portal_pin: IIdentityIntegrationPortalPinSummary;
  community_ids: number[];
  linked_identity_count: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface IIdentityIntegrationCreate {
  name: string;
  slug: string;
  protocol: string;
}

export interface IIdentityIntegrationUpdate {
  name?: string;
  slug?: string;
  issuer?: string | null;
  idp_sso_url?: string | null;
  sp_entity_id?: string | null;
  jit_provisioning_enabled?: boolean;
  community_resolution_mode?: string;
}

export interface ILinkedIdentity {
  id: number;
  user_id: number;
  link_method: string;
  email_at_link_time: string | null;
  linked_at: string | null;
}

export interface IIdentityIntegrationAuditEntry {
  id: number;
  action: string;
  old_data: { [key: string]: any } | null;
  new_data: { [key: string]: any } | null;
  user_id: number | null;
  changed_by_name: string | null;
  ip: string | null;
  created_at: string | null;
}

export interface IIdentityIntegrationPage<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
}

export interface IIdentityIntegrationListParams {
  page?: number;
  perPage?: number;
  status?: string | null;
  protocol?: string | null;
  search?: string | null;
}

export interface IApiFieldError {
  title: string;
  ref: string | null;
}

const base = settings.endpoint + 'settings/identity-integrations';

function unwrap(response: any): any {
  const outer = response && response.data;
  return outer && typeof outer === 'object' && 'data' in outer ? outer.data : outer;
}

/**
 * Fills in the parts of the payload an older backend does not send yet.
 *
 * During a rolling deploy this frontend can be newer than the API for a few
 * minutes, and a component reading `community_resolution.mode` off an
 * undefined field throws during render, which blanks the whole card — the
 * worst possible failure for a settings screen, because it looks like the
 * feature is missing rather than briefly unavailable.
 *
 * The defaults are the real ones, not placeholders: an integration whose API
 * does not know about Community resolution resolves exactly as `fixed` did
 * before it existed, and one with no attribute mapping has an empty list.
 */
function normalizeIntegration(integration: any): IIdentityIntegration {
  const mapping = (integration && integration.identity_mapping) || {};
  const resolution = (integration && integration.community_resolution) || {};
  const portalPin = (integration && integration.portal_pin) || {};

  return Object.assign({}, integration, {
    identity_mapping: {
      attributes: mapping.attributes || [],
      email_attribute: mapping.email_attribute === undefined ? null : mapping.email_attribute,
    },
    community_resolution: {
      mode: resolution.mode || COMMUNITY_RESOLUTION_FIXED,
      mapping_count: resolution.mapping_count || 0,
    },
    // An API without the portal PIN endpoints reports "no PIN configured",
    // which is the truth for it, rather than throwing on an absent field.
    portal_pin: {
      configured: !!portalPin.configured,
      usable: !!portalPin.usable,
    },
  }) as IIdentityIntegration;
}

function unwrapIntegration(response: any): IIdentityIntegration {
  return normalizeIntegration(unwrap(response));
}

function unwrapPage<T>(response: any, fallbackPerPage: number): IIdentityIntegrationPage<T> {
  const page = unwrap(response);

  if (page && Array.isArray(page.data)) {
    return {
      data: page.data,
      total: page.total || 0,
      page: page.page || 1,
      per_page: page.per_page || fallbackPerPage,
    };
  }

  return { data: [], total: 0, page: 1, per_page: fallbackPerPage };
}

function listQuery(params: IIdentityIntegrationListParams): any {
  const query: any = {
    page: params.page || 1,
    perPage: params.perPage || 15,
  };

  if (params.status) {
    query.status = params.status;
  }
  if (params.protocol) {
    query.protocol = params.protocol;
  }
  if (params.search) {
    query.search = params.search;
  }

  return query;
}

export function apiFieldErrors(error: any): IApiFieldError[] {
  const body = error && error.response && error.response.data;
  const errors = body && body.errors;

  if (!Array.isArray(errors)) {
    return [];
  }

  return errors
    .filter((each: any) => each && each.ref !== 'DEBUGexceptionMessage')
    .map((each: any) => ({ title: String(each.title), ref: each.ref === undefined ? null : each.ref }));
}

export function apiErrorStatus(error: any): number | null {
  const status = error && error.response && error.response.status;
  return typeof status === 'number' ? status : null;
}

export const identityIntegrationsService = {
  list(params: IIdentityIntegrationListParams): Promise<IIdentityIntegrationPage<IIdentityIntegration>> {
    return axios({ url: base, method: 'GET', params: listQuery(params) })
      .then((response: any) => {
        const page = unwrapPage<IIdentityIntegration>(response, params.perPage || 15);
        page.data = page.data.map(normalizeIntegration);

        return page;
      });
  },

  get(id: number): Promise<IIdentityIntegration> {
    return axios({ url: base + '/' + id, method: 'GET' }).then(unwrapIntegration);
  },

  create(payload: IIdentityIntegrationCreate): Promise<IIdentityIntegration> {
    return axios({ url: base, method: 'POST', data: payload }).then(unwrapIntegration);
  },

  update(id: number, changes: IIdentityIntegrationUpdate): Promise<IIdentityIntegration> {
    return axios({ url: base + '/' + id, method: 'PATCH', data: changes }).then(unwrapIntegration);
  },

  enable(id: number): Promise<IIdentityIntegration> {
    return axios({ url: base + '/' + id + '/enable', method: 'POST' }).then(unwrapIntegration);
  },

  disable(id: number): Promise<IIdentityIntegration> {
    return axios({ url: base + '/' + id + '/disable', method: 'POST' }).then(unwrapIntegration);
  },

  replaceScope(id: number, communityIds: number[]): Promise<IIdentityIntegration> {
    return axios({
      url: base + '/' + id + '/scope',
      method: 'PUT',
      data: { community_ids: communityIds },
    }).then(unwrapIntegration);
  },

  replaceAttributeMappings(
    id: number,
    attributes: IIdentityAttributeMapping[],
    confirmExternalSubjectChange: boolean = false
  ): Promise<IIdentityIntegration> {
    return axios({
      url: base + '/' + id + '/attribute-mappings',
      method: 'PUT',
      data: { attributes, confirm_external_subject_change: confirmExternalSubjectChange },
    }).then(unwrapIntegration);
  },

  communityMappings(
    id: number,
    page: number,
    perPage: number,
    search?: string | null
  ): Promise<IIdentityIntegrationPage<IIdentityCommunityMapping>> {
    const params: any = { page, perPage };

    if (search) {
      params.search = search;
    }

    return axios({ url: base + '/' + id + '/community-mappings', method: 'GET', params })
      .then((response: any) => unwrapPage<IIdentityCommunityMapping>(response, perPage));
  },

  saveCommunityMapping(
    id: number,
    mappingId: number | null,
    externalValue: string,
    communityId: number
  ): Promise<IIdentityIntegration> {
    const url = base + '/' + id + '/community-mappings' + (mappingId ? '/' + mappingId : '');

    return axios({
      url,
      method: mappingId ? 'PUT' : 'POST',
      data: { external_value: externalValue, community_id: communityId },
    }).then(unwrapIntegration);
  },

  deleteCommunityMapping(id: number, mappingId: number): Promise<IIdentityIntegration> {
    return axios({ url: base + '/' + id + '/community-mappings/' + mappingId, method: 'DELETE' })
      .then(unwrapIntegration);
  },

  validation(id: number): Promise<IIdentityIntegrationValidation> {
    return axios({ url: base + '/' + id + '/validation', method: 'GET' })
      .then((response: any) => unwrap(response) as IIdentityIntegrationValidation);
  },

  replaceCertificate(id: number, certificate: string): Promise<IIdentityIntegration> {
    return axios({
      url: base + '/' + id + '/certificate',
      method: 'PUT',
      data: { idp_certificate: certificate },
    }).then(unwrapIntegration);
  },

  portalPin(id: number): Promise<IIdentityIntegrationPortalPin> {
    return axios({ url: base + '/' + id + '/portal-pin', method: 'GET' })
      .then((response: any) => unwrap(response) as IIdentityIntegrationPortalPin);
  },

  replacePortalPin(id: number, pin: string): Promise<IIdentityIntegrationPortalPin> {
    return axios({ url: base + '/' + id + '/portal-pin', method: 'PUT', data: { pin } })
      .then((response: any) => unwrap(response) as IIdentityIntegrationPortalPin);
  },

  regeneratePortalPin(id: number): Promise<IIdentityIntegrationPortalPin> {
    return axios({ url: base + '/' + id + '/portal-pin/regenerate', method: 'POST' })
      .then((response: any) => unwrap(response) as IIdentityIntegrationPortalPin);
  },

  clearPortalPin(id: number): Promise<IIdentityIntegrationPortalPin> {
    return axios({ url: base + '/' + id + '/portal-pin', method: 'DELETE' })
      .then((response: any) => unwrap(response) as IIdentityIntegrationPortalPin);
  },

  identities(id: number, page: number, perPage: number): Promise<IIdentityIntegrationPage<ILinkedIdentity>> {
    return axios({
      url: base + '/' + id + '/identities',
      method: 'GET',
      params: { page, perPage },
    }).then((response: any) => unwrapPage<ILinkedIdentity>(response, perPage));
  },

  auditLog(
    id: number,
    page: number,
    perPage: number,
    action?: string | null
  ): Promise<IIdentityIntegrationPage<IIdentityIntegrationAuditEntry>> {
    const params: any = { page, perPage };

    if (action) {
      params.action = action;
    }

    return axios({ url: base + '/' + id + '/audit-log', method: 'GET', params })
      .then((response: any) => unwrapPage<IIdentityIntegrationAuditEntry>(response, perPage));
  },
};
